import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, UtensilsCrossed, Calendar,
  BarChart3, DollarSign, Users, Settings, Bell, LogOut,
  ChevronLeft, ChevronRight, Menu, X, Star, User,
} from 'lucide-react';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const NAV = [
  { to: '/kitchen/dashboard',   label: 'Dashboard',    icon: LayoutDashboard,  perm: null },
  { to: '/kitchen/orders',      label: 'Orders',       icon: ShoppingBag,      perm: 'orders' },
  { to: '/kitchen/menu',        label: 'Menu',         icon: UtensilsCrossed,  perm: 'menu' },
  { to: '/kitchen/schedule',    label: 'Schedule',     icon: Calendar,         perm: 'schedule' },
  { to: '/kitchen/analytics',   label: 'Analytics',    icon: BarChart3,        perm: 'analytics' },
  { to: '/kitchen/finance',     label: 'Finance',      icon: DollarSign,       perm: 'finance' },
  { to: '/kitchen/team',        label: 'Team',         icon: Users,            perm: 'team' },
  { to: '/kitchen/ratings',     label: 'Ratings',      icon: Star,             perm: 'ratings' },
  { to: '/kitchen/notifications', label: 'Notifications', icon: Bell,          perm: 'support' },
  { to: '/kitchen/settings',    label: 'Settings',     icon: Settings,         perm: 'settings' },
];

const ROLE_LABELS = {
  kitchen:            'Kitchen Owner',
  super_admin:        'Super Admin',
  admin:              'Admin',
  kitchen_manager:    'Kitchen Manager',
  finance_manager:    'Finance Manager',
  support_executive:  'Support Exec',
  operations_manager: 'Ops Manager',
};

export default function KitchenLayout() {
  const { user, userRole, logout, can } = useKitchenAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed]     = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [profileDrop, setProfileDrop] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 1024) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/kitchen/login');
  };

  const initials = () => {
    const name = user?.kitchen_name || user?.full_name || user?.username || 'K';
    return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };

  const displayName = user?.kitchen_name || user?.full_name || user?.username || 'Kitchen';
  const roleLabel   = ROLE_LABELS[userRole] || userRole || 'Staff';

  const visibleNav = NAV.filter(n => !n.perm || can[n.perm]);

  return (
    <div className="k-shell">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 199 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav className={`k-sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
        {/* Brand */}
        <div className="k-sidebar-brand">
          <img
            src="/biryani-logo.png"
            alt="Biryani"
            className="k-sidebar-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="k-brand-text">
            <div className="k-brand-name">Biryani</div>
            <div className="k-brand-badge">Kitchen Portal</div>
          </div>
        </div>

        {/* Nav */}
        <div className="k-sidebar-nav">
          <div className="k-nav-section-label">Main Menu</div>
          {visibleNav.map(({ to, label, icon: Icon, perm }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `k-nav-item${isActive ? ' active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="k-nav-icon"><Icon size={18} /></span>
              <span className="k-nav-label">{label}</span>
              {label === 'Notifications' && unreadCount > 0 && (
                <span className="k-nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Footer / User */}
        <div className="k-sidebar-footer">
          <div className="k-sidebar-user" ref={profileRef} onClick={() => setProfileDrop(p => !p)}>
            <div className="k-avatar">{initials()}</div>
            <div className="k-user-info">
              <div className="k-user-name">{displayName}</div>
              <div className="k-user-role">{roleLabel}</div>
            </div>

            {profileDrop && (
              <div className="k-dropdown-menu" style={{ bottom: '100%', top: 'auto', right: 0, left: 0, marginBottom: 8 }}>
                <button className="k-dropdown-item" onClick={() => { navigate('/kitchen/settings'); setProfileDrop(false); }}>
                  <User size={16} /> Profile & Settings
                </button>
                <div className="k-dropdown-sep" />
                <button className="k-dropdown-item danger" onClick={handleLogout}>
                  <LogOut size={16} /> Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Collapse toggle (desktop only) */}
          <button
            className="k-btn-icon"
            style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 8, padding: '8px 10px' }}
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span style={{ fontSize: 12, color: 'var(--k-text-sub)', whiteSpace: 'nowrap' }}>Collapse</span></>}
          </button>
        </div>
      </nav>

      {/* Main */}
      <div className={`k-main${collapsed ? ' collapsed' : ''}`}>
        {/* Header */}
        <header className="k-header">
          {/* Mobile menu toggle */}
          <button
            className="k-btn-icon"
            style={{ display: 'none' }}
            id="k-mobile-toggle"
            onClick={() => setMobileOpen(o => !o)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <style>{`
            @media (max-width: 1024px) { #k-mobile-toggle { display: flex !important; } }
          `}</style>

          <div className="k-header-brand">
            <img
              src="/biryani-logo.png"
              alt="Biryani"
              className="k-header-logo-img"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>

          <div className="k-header-actions">
            <NavLink to="/kitchen/notifications" style={{ textDecoration: 'none' }}>
              <button className="k-btn-icon" style={{ position: 'relative' }}>
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    background: 'var(--k-danger)', color: '#fff',
                    borderRadius: '50%', width: 14, height: 14,
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
            </NavLink>

            <div className="k-avatar-sm" onClick={() => navigate('/kitchen/settings')} style={{ cursor: 'pointer' }} title="Go to Settings">
              {initials()}
            </div>

            <button 
              className="k-btn-icon" 
              onClick={handleLogout}
              title="Sign out"
              style={{ color: 'var(--k-danger)' }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="k-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
